<script setup lang="ts">
import { AlertIcon, CheckmarkCircleIcon, DismissIcon, InfoIcon } from '@blpde/nx-icons';
import { computed, inject, nextTick, onBeforeUnmount, ref, useTemplateRef, watch } from 'vue';
import { NxButton } from '../Button';
import {
  nxToastHostInheritKey,
  useNxToastHost,
  useNxToasts,
  type NxToast,
  type NxToastKind,
} from './useNxToasts';

export type NxToastPlacement =
  | 'bottom-right'
  | 'bottom-center'
  | 'bottom-left'
  | 'top-right'
  | 'top-center'
  | 'top-left';

const props = withDefaults(
  defineProps<{
    placement?: NxToastPlacement;
    /** Accessible label of the dismiss control on dismissible toasts. */
    dismissLabel?: string;
  }>(),
  {
    placement: 'bottom-right',
    dismissLabel: 'Dismiss',
  }
);

const { toasts, dismiss, runAction } = useNxToasts();
// A modal's own host (see NxModalToastHost) looks like the one it covers.
const { isTop: isTopHost, look } = useNxToastHost(
  () => ({ placement: props.placement, dismissLabel: props.dismissLabel }),
  inject(nxToastHostInheritKey, false)
);
const hostRef = useTemplateRef<HTMLElement>('host-ref');

const icons = {
  success: CheckmarkCircleIcon,
  error: AlertIcon,
  info: InfoIcon,
} as const;

function icon(kind: NxToastKind) {
  return icons[kind];
}

/* ── Countdown ─────────────────────────────────────────────────────────── */

// A shared clock ticked only while a countdown toast is on screen; the
// seconds label derives from it, the bar animates in CSS from `--toast-duration`.
const now = ref(Date.now());
let clock: number | null = null;

const hasCountdown = computed(() => toasts.value.some((t) => t.countdown && t.expiresAt !== null));

watch(
  hasCountdown,
  (active) => {
    if (active && clock === null) {
      now.value = Date.now();
      clock = window.setInterval(() => {
        now.value = Date.now();
      }, 250);
    } else if (!active && clock !== null) {
      window.clearInterval(clock);
      clock = null;
    }
  },
  { immediate: true }
);

function remainingSeconds(t: NxToast): number {
  if (t.expiresAt === null) return 0;
  return Math.max(0, Math.ceil((t.expiresAt - now.value) / 1000));
}

function toastStyle(t: NxToast) {
  return t.countdown ? { '--toast-duration': `${t.timeout}ms` } : undefined;
}

/* ── Top layer ─────────────────────────────────────────────────────────── */

/** Popover API puts the host in the top layer (above open <dialog>s) without a
 *  z-index. Guarded — jsdom and old engines have no showPopover. */
function showTopLayer(open: boolean): void {
  const el = hostRef.value;
  if (!el || !('showPopover' in el)) return;
  try {
    if (open) el.showPopover();
    else el.hidePopover();
  } catch {
    /* already in the requested state */
  }
}

const hasToasts = computed(() => isTopHost.value && toasts.value.length > 0);

watch(hasToasts, (open) => {
  // Show as soon as a toast appears; hiding waits for the leave transition
  // (see onAfterLeave) so the last toast can animate out.
  if (open) void nextTick(() => showTopLayer(true));
});

function onAfterLeave() {
  if (!hasToasts.value) showTopLayer(false);
}

onBeforeUnmount(() => showTopLayer(false));

function onToastClick(t: NxToast) {
  if (t.dismissible) dismiss(t.id);
}
</script>

<template>
  <div
    v-if="isTopHost"
    ref="host-ref"
    class="nx-toast-host"
    :class="`placement-${look.placement}`"
    popover="manual"
  >
    <TransitionGroup name="nx-toast" @after-leave="onAfterLeave">
      <div
        v-for="t in toasts"
        :key="t.id"
        role="status"
        aria-live="polite"
        class="nx-toast"
        :class="[t.kind, { dismissible: t.dismissible, 'has-action': Boolean(t.action) }]"
        :style="toastStyle(t)"
        @click="onToastClick(t)"
      >
        <component :is="icon(t.kind)" filled class="nx-toast-icon nxt-icon-md" />
        <span class="nx-toast-message">{{ t.message }}</span>
        <span v-if="t.countdown" class="nx-toast-countdown">{{ remainingSeconds(t) }} s</span>
        <NxButton
          v-if="t.action"
          class="nx-toast-action"
          height="xs"
          variant="ghost"
          @click.stop="runAction(t.id)"
        >
          {{ t.action.label }}
        </NxButton>
        <button
          v-if="t.dismissible"
          type="button"
          class="nx-toast-dismiss"
          :aria-label="look.dismissLabel"
          @click.stop="dismiss(t.id)"
        >
          <DismissIcon class="nxt-icon-sm" aria-hidden="true" />
        </button>
        <span v-if="t.countdown" class="nx-toast-bar" aria-hidden="true"></span>
      </div>
    </TransitionGroup>
  </div>
</template>

<style scoped>
.nx-toast-host {
  position: fixed;
  /* Neutralise the UA popover styles (inset:0 / margin:auto / border / bg)
     so the placement classes below position the stack. */
  inset: auto;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  overflow: visible;
  width: auto;
  height: auto;
  z-index: var(--nxt-z-toast);
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-xs);
  pointer-events: none;
}
.nx-toast-host.placement-bottom-right,
.nx-toast-host.placement-bottom-center,
.nx-toast-host.placement-bottom-left {
  bottom: var(--nxt-space-lg);
}
.nx-toast-host.placement-top-right,
.nx-toast-host.placement-top-center,
.nx-toast-host.placement-top-left {
  top: var(--nxt-space-lg);
}
.nx-toast-host.placement-bottom-right,
.nx-toast-host.placement-top-right {
  right: var(--nxt-space-lg);
}
.nx-toast-host.placement-bottom-left,
.nx-toast-host.placement-top-left {
  left: var(--nxt-space-lg);
}
.nx-toast-host.placement-bottom-center,
.nx-toast-host.placement-top-center {
  left: 50%;
  transform: translateX(-50%);
  align-items: center;
}

.nx-toast {
  position: relative;
  overflow: hidden;
  pointer-events: auto;
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  padding: var(--nxt-space-sm) var(--nxt-space-md);
  border: var(--nxt-hairline) solid var(--nxt-toast-border);
  border-left: calc(var(--nxt-hairline) * 3) solid var(--nxt-brand-bg);
  border-radius: var(--nxt-radius-sm);
  background: var(--nxt-toast-bg);
  box-shadow: var(--nxt-toast-shadow);
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-medium);
  color: var(--nxt-toast-fg);
  text-align: left;
  max-width: min(var(--nxt-panel-width-xl), 92vw);
}

.nx-toast.dismissible {
  cursor: pointer;
}

.nx-toast-icon {
  flex-shrink: 0;
  color: var(--nxt-brand-bg);
}

.nx-toast-message {
  min-width: 0;
}

.nx-toast-countdown {
  flex-shrink: 0;
  font-variant-numeric: tabular-nums;
  color: var(--nxt-fg-muted);
}

.nx-toast-action {
  flex-shrink: 0;
  margin-left: var(--nxt-space-2xs);
}

.nx-toast-dismiss {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--nxt-control-size-xs);
  height: var(--nxt-control-size-xs);
  flex-shrink: 0;
  border: none;
  border-radius: var(--nxt-radius-sm);
  background: transparent;
  color: var(--nxt-fg-muted);
  padding: 0;
  cursor: pointer;
  line-height: 0;
  &:hover {
    color: var(--nxt-fg);
    background: var(--nxt-surface-sunken-bg, transparent);
  }
}

/* Countdown bar: shrinks from full width to zero over the toast's timeout. */
.nx-toast-bar {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: calc(var(--nxt-hairline) * 2);
  transform-origin: left center;
  background: var(--nxt-brand-bg);
  animation: nx-toast-countdown var(--toast-duration, 3000ms) linear forwards;
}

@keyframes nx-toast-countdown {
  from {
    transform: scaleX(1);
  }
  to {
    transform: scaleX(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .nx-toast-bar {
    animation: none;
  }
}

/* Per-kind accent drives the left border, the icon and the countdown bar. */
.nx-toast.success {
  --toast-accent: var(--nxt-success-bg);
}
.nx-toast.error {
  --toast-accent: var(--nxt-danger-bg);
}
.nx-toast.info {
  --toast-accent: var(--nxt-info-bg);
}
.nx-toast.success,
.nx-toast.error {
  border-left-color: var(--toast-accent);
}
.nx-toast.success .nx-toast-icon,
.nx-toast.error .nx-toast-icon,
.nx-toast.info .nx-toast-icon {
  color: var(--toast-accent);
}
.nx-toast.success .nx-toast-bar,
.nx-toast.error .nx-toast-bar,
.nx-toast.info .nx-toast-bar {
  background: var(--toast-accent);
}

.nx-toast-enter-active,
.nx-toast-leave-active {
  transition: all var(--nxt-motion-base) ease;
}
.placement-bottom-right .nx-toast-enter-from,
.placement-bottom-right .nx-toast-leave-to,
.placement-top-right .nx-toast-enter-from,
.placement-top-right .nx-toast-leave-to {
  opacity: 0;
  transform: translateX(1em);
}
.placement-bottom-left .nx-toast-enter-from,
.placement-bottom-left .nx-toast-leave-to,
.placement-top-left .nx-toast-enter-from,
.placement-top-left .nx-toast-leave-to {
  opacity: 0;
  transform: translateX(-1em);
}
.placement-bottom-center .nx-toast-enter-from,
.placement-bottom-center .nx-toast-leave-to {
  opacity: 0;
  transform: translateY(1em);
}
.placement-top-center .nx-toast-enter-from,
.placement-top-center .nx-toast-leave-to {
  opacity: 0;
  transform: translateY(-1em);
}
</style>
